import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { updateField } from "@/features/configuration/slice";
import { useGetLayerVersionsQuery } from "@/features/shell/api";
import {
  selectCurrentShopId,
  selectLayerVersionName,
  selectSavedLayerVersionName,
} from "@/features/shell/legacySelectors";
import type { LayerVersion } from "@/shared/types/layer";
import type { Shop } from "@/shared/types/shop";

interface SavedOverride {
  shopId: Shop["id"] | undefined;
  /** Valore salvato in lista catturato al markSaved: se cambia, l'override non vale piu'. */
  previousSaved: string | undefined;
  name: string;
}

/**
 * Versione del layer della barra di salvataggio, derivata dallo store (SaveChanges.getVersions/onChangeVersion).
 * Dopo un salvataggio la "salvata" vale la corrente finche' lo shop o la sua versione in lista non cambiano.
 */
export function useLayerVersions() {
  const dispatch = useAppDispatch();
  const { data: versions = [] } = useGetLayerVersionsQuery();
  const shopId = useAppSelector(selectCurrentShopId);
  const editingName = useAppSelector(selectLayerVersionName);
  const savedInList = useAppSelector(selectSavedLayerVersionName);
  const [override, setOverride] = useState<SavedOverride | null>(null);

  const current =
    (editingName && versions.find((version) => version.versionName === editingName)) || versions[0] || null;
  const savedVersion =
    override && override.shopId === shopId && override.previousSaved === savedInList
      ? override.name
      : savedInList;
  const versionChanged = current ? savedVersion !== current.versionName : false;

  const selectVersion = useCallback(
    (version: LayerVersion) => {
      dispatch(updateField({ path: ["setup", "config", "layerVersion"], value: version.versionName }));
    },
    [dispatch],
  );

  const markSaved = useCallback(() => {
    if (current) setOverride({ shopId, previousSaved: savedInList, name: current.versionName });
  }, [current, savedInList, shopId]);

  return { versions, current, savedVersion, versionChanged, selectVersion, markSaved };
}
